import React, { useRef, useState } from 'react';

import RiskOrderSwiper from './risk-order-swpier.component';

import RiskOrderListTable from './risk-order-list-table.component';

import { MobxObjectsManager } from '../mobx-objs/mobx-manager';

import MainPageState from '../main/main-page-states';

import { observer } from 'mobx-react-lite';

import { useCurrentRestaurantCheck } from '../../hooks/useCurrentRestaurantCheck';

import { ErrorMessageBox } from '../data-analysis/data-analysis.page';

import { Box, Typography } from '@mui/material';

import i18n from '../../i18n';

import GlobalNotification from '../../component/common/snackbar';

import { RiskEvent, RiskOrder } from '../../models';

import RiskOrderDetailDialog, { RiskOrderDetailDialogType } from './risk-order-detail-dialog';

import RiskEventDetailDialog from './risk-event-detail-dialog';

import RiskOrderActionBar from './risk-order-action-bar.component';

import riskOrderListState from './risk-order-list-state';

import AiAnalysisDialog from './ai-analysis-dialog.component';

import PeriodDateFilter from '../../component/period-date-filter.component';

import { ConditionsBox, SearchKeyInput } from '../../component/form/search-key-input.component';

import { dateValidate, validateCurrentDate } from '../../constants/tool-order';

import moment from 'moment';

import { getRiskOrderListFilterItems } from './risk-order-list-filter-items';
import RiskOrderViewModeIconButtons from './risk-order-view-mode-icon-buttons.component';
import RiskOrderDataModeButtons from './risk-order-data-mode-buttons.component';
import './scss/risk-order-list.style.scss';

const mainPageData = MobxObjectsManager.get(MainPageState);



const RiskOrderList: React.FC = observer(() => {

    const { tunnelState, tunnelStateText } = useCurrentRestaurantCheck();



    /** 与 SearchKeyInput 的 useImperativeHandle 对齐，用于删除筛选 Chip 后重置二级下拉 */

    const searchKeyInputRef = useRef<{ setSelectKey: (val: string) => void } | null>(null);



    /** 日期选择器受控值，与云端风险订单列表一致 */

    const [viewDateTimeData, setViewDateTimeData] = useState<{

        startDate: Date | null;

        endDate: Date | null;

    }>(() => {

        const { startTime, endTime } = validateCurrentDate();

        return {

            startDate: moment(startTime, 'YYYY-MM-DD HH:mm').toDate(),

            endDate: moment(endTime, 'YYYY-MM-DD HH:mm').toDate(),

        };

    });



    // 初始化

    React.useEffect(() => {

        riskOrderListState.initialize(mainPageData.currentRestaurant.string_id ?? '');

    }, [mainPageData.currentRestaurant]);



    /**

     * 周期/自定义日期范围变更，重新请求即时列表（后端 start_time / end_time）

     */

    const handlePeriodDateChange = (dates: [Date | null, Date | null]) => {

        setViewDateTimeData({

            startDate: dates[0],

            endDate: dates[1],

        });

        if (dates[0] && dates[1] && dateValidate(dates[0], dates[1])) {

            riskOrderListState.setDateRange(dates[0], dates[1]);

        }

    };



    // 订单点击处理

    const handleOrderClick = (order: RiskOrder) => {
        riskOrderListState.openOrderDialog({
            ...order,
            risk_status: order.orderStatus ?? order.risk_status,
        });
    };



    // 事件点击处理

    const handleEventClick = (event: unknown) => {

        riskOrderListState.openEventDialog(event as RiskEvent);

    };



    // 隧道服务不存在 / 检查中 / 配置不正确等，都当成错误视图处理

    if (!tunnelState && tunnelStateText) {

        return <ErrorMessageBox errorMessage={tunnelStateText} height={500} />;

    }



    const isCardView = riskOrderListState.viewMode === 'card';

    const showEmpty = !riskOrderListState.loading && riskOrderListState.totalAbnormalCount === 0;

    /** 汇总数字：加载中与日营业汇总一致显示 -- */
    const summaryCountText = (value: number) => (riskOrderListState.loading ? '--' : String(value));

    const summaryOverviewText =
        `${i18n.t('intelligent_risk_control_page_summary_total_label')}：${summaryCountText(riskOrderListState.totalAbnormalCount)} | ` +
        `${i18n.t('intelligent_risk_control_page_text_abnormal_orders')}：${summaryCountText(riskOrderListState.abnormalOrderCount)} | ` +
        `${i18n.t('intelligent_risk_control_page_text_abnormal_events')}：${summaryCountText(riskOrderListState.abnormalEventCount)}`;

    return (

        <Box

            className="risk-order-list"

        >

            {/* 工具栏：左侧统计；右侧日期/筛选/视图切换 */}

            <Box

                display="flex"

                justifyContent="space-between"

                alignItems="center"

                gap={2}

                rowGap={2}

                flexWrap="wrap"

                sx={{ mb: 2 }}

            >

                <Typography className="risk-order-list-overview-text">
                    {summaryOverviewText}
                </Typography>

                <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} rowGap={2} flexWrap="wrap">

                    <RiskOrderDataModeButtons />

                    <PeriodDateFilter

                        startDate={riskOrderListState.startTime}

                        endDate={riskOrderListState.endTime}

                        pickerValue={[viewDateTimeData.startDate, viewDateTimeData.endDate]}

                        onDateTimeChange={handlePeriodDateChange}

                    />

                    <SearchKeyInput

                        ref={searchKeyInputRef}

                        pillSelectStyle

                        showConditionsBox={false}

                        items={getRiskOrderListFilterItems({ includeStatus: false })}

                        conditions={riskOrderListState.conditions}

                        onSearch={(value) => riskOrderListState.setConditions(value)}

                    />

                    <RiskOrderViewModeIconButtons />

                    <RiskOrderActionBar

                        exportDisabled={riskOrderListState.loading}

                        exportPreCheck={() => {

                            if (riskOrderListState.orders.length === 0) {

                                GlobalNotification.warning(i18n.t('general_text_no_data'));

                                return false;

                            }

                            return true;

                        }}

                        onExport={() => {

                            riskOrderListState.downloadExcel();

                        }}

                        onAIAnalysisClick={() => {

                            void riskOrderListState.openAiAnalysisDialog();

                        }}

                    />

                </Box>

            </Box>

            {riskOrderListState.conditions.length > 0 ? (

                <Box sx={{ mb: 2, width: '100%' }}>

                    <ConditionsBox

                        conditions={riskOrderListState.conditions}

                        onDelete={(next) => {

                            riskOrderListState.setConditions(next);

                            searchKeyInputRef.current?.setSelectKey?.('');

                        }}

                    />

                </Box>

            ) : null}

            {isCardView && riskOrderListState.orders.length > 0 && (

                <RiskOrderSwiper

                    orders={riskOrderListState.orders}

                    onOrderClick={handleOrderClick}

                    onEventClick={handleEventClick}

                />

            )}

            {!isCardView && !showEmpty && <RiskOrderListTable />}

            {showEmpty && (

                <ErrorMessageBox errorMessage={i18n.t('general_text_no_data')} height={500} />

            )}



            <RiskOrderDetailDialog

                open={riskOrderListState.orderDialogOpen}

                onClose={() => riskOrderListState.closeOrderDialog()}

                orderSummary={riskOrderListState.selectedOrder}

                stringId={mainPageData.currentRestaurant.string_id}

                onSaved={() => riskOrderListState.fetchData()}

                type={RiskOrderDetailDialogType.ORDER}

                mode={riskOrderListState.riskDataMode}

            />



            <RiskEventDetailDialog

                open={riskOrderListState.eventDialogOpen}

                onClose={() => riskOrderListState.closeEventDialog()}

                eventSummary={riskOrderListState.selectedEvent}

                stringId={mainPageData.currentRestaurant.string_id}

                onSaved={() => riskOrderListState.fetchData()}

            />

            {/* ai数据智能分析弹窗 */}

            <AiAnalysisDialog

                open={riskOrderListState.aiAnalysisDialogOpen}

                stringId={riskOrderListState.stringId}

                onClose={() => riskOrderListState.closeAiAnalysisDialog()}

            />

        </Box>

    );

});



export default RiskOrderList;

